import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { createInvoiceLink, uploadInvoice } from '@/api/invoices';
import { useAuth } from '@/auth/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/** Upload one or more documents and attach them to a single transaction in
 *  one motion. The first consumer is the finding detail page: a practitioner
 *  looking at a finding drops the invoice here, it uploads to the customer's
 *  invoice pool (`uploadInvoice`, sha256-deduped) and links to this
 *  transaction (`createInvoiceLink`, idempotent on the link tuple). Once
 *  attached, the sibling `InvoicePreview` renders it and its Edit control
 *  captures the SOR/91-45 facts (the "label" step) — so this panel owns
 *  upload + attach and leaves labelling to the preview it feeds.
 *
 *  Invalidating the shared `['transaction-documents', customerSlug]` key is
 *  what makes the preview pick up the new link without a reload. */
export function AttachInvoicePanel({
  customerSlug,
  transactionId,
}: {
  customerSlug: string | null;
  transactionId: string;
}) {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [failedNames, setFailedNames] = useState<string[]>([]);

  const attach = useMutation({
    mutationFn: async (files: File[]) => {
      const token = (await getIdToken()) ?? '';
      let attached = 0;
      const failed: string[] = [];
      for (const file of files) {
        try {
          const doc = await uploadInvoice(customerSlug!, file, token);
          await createInvoiceLink(
            customerSlug!,
            { external_transaction_id: transactionId, source_document_id: doc.id },
            token,
          );
          attached += 1;
        } catch {
          failed.push(file.name);
        }
      }
      return { attached, failed };
    },
    onSuccess: ({ attached, failed }) => {
      setReport(attached > 0 ? `${attached} attached to this transaction` : null);
      setFailedNames(failed);
      void qc.invalidateQueries({ queryKey: ['transaction-documents', customerSlug] });
    },
  });

  const handleFiles = useCallback(
    (list: FileList | File[]) => {
      const files = Array.from(list);
      if (files.length === 0 || customerSlug === null) return;
      setReport(null);
      setFailedNames([]);
      attach.mutate(files);
    },
    [attach, customerSlug],
  );

  const disabled = customerSlug === null || attach.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Attach an invoice</CardTitle>
        <CardDescription>
          Upload the supporting document for this transaction. It attaches here immediately;
          capture the invoice facts with the Edit control once it appears above.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-1.5">
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onClick={() => {
            if (!disabled) inputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!disabled) handleFiles(e.dataTransfer.files);
          }}
          className={cn(
            'flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal',
            disabled && 'cursor-not-allowed opacity-60',
            dragOver
              ? 'border-brand-royal bg-brand-royal/5 text-brand-navy'
              : 'border-border bg-muted/20 text-brand-slate hover:border-brand-royal/50',
          )}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {attach.isPending
            ? 'Uploading…'
            : (report ?? 'Drop invoice files here (PDF, images, anything) or click to browse')}
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            disabled={disabled}
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        {failedNames.length > 0 && (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
          >
            {failedNames.length} file{failedNames.length === 1 ? '' : 's'} failed to attach —
            retry: {failedNames.join(', ')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
